import { useIsMutating } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Controller } from "react-hook-form";

import { Button } from "@/components/button";
import { FieldError, Input, Label, TextField } from "@/components/field";
import { toast } from "@/components/toast/toast-queue";
import { AuthContainer } from "@/features/auth/components/AuthContainer";

import { PasswordRequirements } from "../forceUpdatePassword/components/PasswordRequeriments";
import { validatePassword } from "../forceUpdatePassword/components/validatePassword";
import {
  type CreatePasswordFormData,
  useCreatePasswordForm,
} from "./hooks/useCreatePasswordForm";
import { useCreatePasswordMutation } from "./hooks/useCreatePasswordMutation";
import { resolveCreatePasswordRedirect } from "./resolveCreatePasswordRedirect";

export function CreatePasswordPage() {
  const navigate = useNavigate();
  const { control, handleSubmit, watch } = useCreatePasswordForm();
  const { mutate } = useCreatePasswordMutation();

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const isPasswordValid = Object.values(
    validatePassword(password, confirmPassword),
  ).every(Boolean);
  const [showPassword, setShowPassword] = useState(false);
  const isLoading = useIsMutating({ mutationKey: ["update-password"] });

  function onSubmit(data: CreatePasswordFormData) {
    mutate(data, {
      onError: (error: Error) => {
        toast.add(
          { title: error.message || "Failed to reset password", type: "error" },
          { timeout: 5000 },
        );
      },
      onSuccess: (result) => {
        toast.add(
          { title: "Password created successfully!", type: "success" },
          { timeout: 5000 },
        );

        const successData = localStorage.getItem("subscriptionSuccessData");
        const parsedData = successData ? JSON.parse(successData) : null;

        navigate(
          resolveCreatePasswordRedirect({
            isPasswordReset: result?.isPasswordReset ?? false,
            planName: result?.planName ?? parsedData?.planName ?? null,
            roleId: result?.roleId,
          }),
        );
      },
    });
  }

  return (
    <AuthContainer
      description="Please create a new password for your account."
      title="Create Password"
    >
      <div className="flex items-center justify-center flex-col relative">
        <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col">
            <Controller
              control={control}
              name="password"
              render={({
                field: { name, onBlur, onChange, ref, value },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  className="w-full"
                  isInvalid={invalid}
                  isRequired
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  validationBehavior="aria"
                  value={value}
                >
                  <Label>New password</Label>
                  <div className="relative mb-6">
                    <Input
                      placeholder="Enter your password"
                      ref={ref}
                      type={showPassword ? "text" : "password"}
                    />
                    <button
                      className="absolute inset-y-0 right-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      type="button"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                      )}
                    </button>
                  </div>
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
              rules={{ required: "New password is required." }}
            />
            <PasswordRequirements
              confirmPassword={confirmPassword}
              password={password}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({
                field: { name, onBlur, onChange, ref, value },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  className="mt-5 w-90"
                  isInvalid={invalid}
                  isRequired
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  validationBehavior="aria"
                  value={value}
                >
                  <Label>Confirm password</Label>
                  <div className="relative mb-6">
                    <Input
                      placeholder="Enter your password"
                      ref={ref}
                      type={showPassword ? "text" : "password"}
                    />
                    <button
                      className="absolute inset-y-0 right-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      type="button"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                      )}
                    </button>
                  </div>
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
              rules={{ required: "Confirm password is required." }}
            />
            <Button isDisabled={!isPasswordValid || !!isLoading} type="submit">
              {isLoading ? "Saving your new password..." : "Update password"}
            </Button>
          </div>
        </form>
      </div>
    </AuthContainer>
  );
}
